import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useProjectService from '../../../app/services/hooks/useProjectService';
import useCostCenterService from '../../../app/services/hooks/useCostCenterService';
import { ProjectDataForm, Product, ProjectUserOption, normalizeProjectPayloadForApi } from '../../../app/models/Project';
import { CostCenterDataForm, CostCenterFilters } from '../../../app/models/CostCenter';
import ProjectFormContainer from './ProjectFormContainer';

interface Props {
  projectId: number;
  onSaved?: () => void;
  onCancel?: () => void;
  onError?: () => void;
}

const NOOP = () => undefined;

const defaultCostCenterFilter: CostCenterFilters = {
  page: 1,
  per_page: 500,
  sort: 'name',
  order: 'asc'
};

const ProjectEdit = ({ projectId, onSaved, onCancel = NOOP, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { editProject, updateProject, fetchingEditProject, fetchingUpdateProject } = useProjectService();
  const { getCostCenters } = useCostCenterService();

  const [project, setProject] = useState<ProjectDataForm>({ name: '', description: '' });
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<ProjectUserOption[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterDataForm[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => {
    edit();
    getCostCenters(defaultCostCenterFilter, {
      onSuccess: (response: ServiceResponse) => {
        setCostCenters(response.data?.cost_centers ?? []);
      }
    });
  }, []);

  const edit = () => {
    showLoading?.('loading', 'Cargando proyecto...');
    editProject(projectId, {
      onSuccess: (response: ServiceResponse) => {
        const projectData = response.data.project;
        const plantedIds = projectData.planted_varieties?.map((v: any) => v.id) || [];
        const userIds = projectData.user_ids ?? [];

        setProject({
          ...projectData,
          planted_variety_ids: plantedIds,
          user_ids: userIds
        });

        setProducts(response.data.products || []);
        setUsers(response.data.users || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        onError && onError();
      }
    });
  };

  const update = () => {
    showLoading?.('loading', 'Actualizando proyecto...');
    updateProject(projectId, normalizeProjectPayloadForApi(project), {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
        onSaved && onSaved();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  return (
    <ProjectFormContainer
      fetching={fetchingEditProject || fetchingUpdateProject}
      action={update}
      cancel={onCancel}
      project={project}
      setProject={setProject}
      products={products}
      users={users}
      costCenters={costCenters}
      errorFields={errorFields}
    />
  );
};

export default ProjectEdit;